import React from 'react';
import {
  Card,
  CardBody,
  Col,
  Input,
  Table,
  Spinner,
  CardFooter,
  Button,
} from 'reactstrap';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { loanPaymentHeaders } from './constants';
import { useTableHook } from '../../../components/hooks';
import { fetchEmployeeLoanEnquiry } from '../../../store/apis/loanService';
import {
  FilterDropdown,
  TableFilterBubbles,
  TableSortDisplayArrow,
  DatePicker,
  TablePagination,
} from '../../../components/Common';
import { AuthUtil } from '../../../utils';
import { fetchTableColumnValues } from '../../../store/apis/commonService';

import { formatAmountWithCommas } from '../../../utils/number.util';
import { Messages } from '../../../common/messages';

import '../index.scss';

function LoanEnquiry() {
  document.title = 'Loan Enquiry | Envision Daikin';
  const navigate = useNavigate();

  // Table Hook
  const {
    data,
    pageRequest,
    setPageRequest,
    externalFilters,
    setExternalFilters,
    resetFilters,
    columnData,
    setColumnName,
    columnName,
    applyFilter,
    handleManualSort,
    clearSingleFilterValue,
    checkColumnIfSorted,
    applyExternalFilters,
    isLoadingData,
    isLoadingColumn,
    nextPage,
    prevPage,
    getPageSize,
    setPageSize,
    handleAutoSort,
  } = useTableHook({
    defaultPageRequest: {
      pageNumber: 1,
      pageSize: 50,
      enquiryType: 'ENQUIRY',
      sortColumn: 'TransNo',
      sortDirection: 'ASC',
      filters: [],
    },
    fetchFn: fetchEmployeeLoanEnquiry,
    fetchColumnFn: fetchTableColumnValues,
    queryKeyPrefix: 'loanEnquiry',
    columnKeyPrefix: 'loanEnquiryColumn',
    tableName: 'LoanEnquiry',
    defaultExternalFilters: {
      enquiryType: 'ENQUIRY',
      employeeId: {
        value: AuthUtil.getAuthUser()?.employeeId,
        label: AuthUtil.getAuthUser()?.userFullName,
      },
    },
  });

  const handleLinkToView = item => {
    navigate(`/loan/apply?ID=${item?.TransNo}`);
  };

  const renderTableBody = () => {
    const tableData = data?.data;

    if (!tableData || tableData.length === 0) {
      return (
        <tr>
          <td className="text-center" colSpan={10}>
            {isLoadingData ? (
              <>
                <Spinner color="dark" size="sm" />
                <span className="ms-2">Loading...</span>
              </>
            ) : (
              Messages.NO_LOAN
            )}
          </td>
        </tr>
      );
    }

    return tableData.map(item => (
      <tr
        key={item.TransNo}
        onClick={() => {
          handleLinkToView(item);
        }}
        className="cursor-pointer"
      >
        <td className="cursor-pointer text-decoration-underline text-primary text-header">
          {item.TransNo}
        </td>
        <td>{item['Loan Type']}</td>
        <td>{item['Payment Date']}</td>
        <td>{item.Curr || '-'}</td>
        <td>{formatAmountWithCommas(item.Amount)}</td>
        <td>{item.PayMode}</td>
        <td>{item.Purpose}</td>
        <td>{item.DeductFrom}</td>
        <td>{item.Installments || '-'}</td>
        <td>{formatAmountWithCommas(item['Outstanding Amt'])}</td>
      </tr>
    ));
  };

  const externalFiltersValidation = () => {
    if (
      externalFilters.fromDate &&
      externalFilters.toDate &&
      new Date(externalFilters?.fromDate) > new Date(externalFilters?.toDate)
    ) {
      toast.error('To Date should be greater than equal to From Date.', {
        className: 'toast-error',
      });
      return false;
    }
    return true;
  };

  return (
    <div className="page-content main-table-page">
      <Card className="enquiry-card">
        <CardBody className="p-0">
          <div className="p-3">
            <span className="fw-semibold fs-5">Loan Enquiry</span>
          </div>
          <div className="external-filter gap-2 mx-3 p-3">
            <Col className="external-filter-col">
              <b>Loan Type</b>
              <Input
                type="select"
                onChange={e =>
                  setExternalFilters(prevFilters => ({
                    ...prevFilters,
                    loanType: e.target.value,
                  }))
                }
                className="external-filter-input"
              >
                <option value="">Select Loan Type</option>
                <option value="01">Housing Advance</option>
                <option value="02">Salary Advance</option>
                <option value="04">Car Loan</option>
              </Input>
            </Col>
            <Col className="external-filter-col">
              <b>Trans No</b>
              <Input
                type="text"
                placeholder="Enter Transaction No"
                onKeyDown={e => {
                  if (e.key === 'Enter') applyExternalFilters();
                }}
                onChange={e =>
                  setExternalFilters(prevFilters => ({
                    ...prevFilters,
                    loanId: e.target.value,
                  }))
                }
                className="external-filter-input"
              />
            </Col>
            <Col className="external-filter-col">
              <b>From Date</b>
              <DatePicker
                onChange={e =>
                  setExternalFilters(prevFilters => ({
                    ...prevFilters,
                    fromDate: e,
                  }))
                }
                className="form-control external-filter-input"
                value={externalFilters?.fromDate || ''}
              />
            </Col>
            <Col className="external-filter-col">
              <b>To Date</b>
              <DatePicker
                onChange={e =>
                  setExternalFilters(prevFilters => ({
                    ...prevFilters,
                    toDate: e,
                  }))
                }
                className="form-control external-filter-input"
                value={externalFilters?.toDate || ''}
              />
            </Col>
            <Col>
              <div className="external-filter-btns gap-2 d-flex justify-content-end">
                <Button
                  type="button"
                  className="custom-btn"
                  onClick={() => {
                    if (!externalFiltersValidation()) {
                      return;
                    }
                    applyExternalFilters({
                      ...externalFilters,
                      employeeId: externalFilters?.employeeId?.value,
                    });
                  }}
                >
                  Search
                </Button>
              </div>
            </Col>
          </div>
          <div className="my-2 px-2">
            <TableFilterBubbles
              allFilters={pageRequest?.filters || []}
              clearSingleFilterValue={clearSingleFilterValue}
              labelMapping={loanPaymentHeaders}
              clearAllFilters={resetFilters}
            />
          </div>
          <div className="table-responsive table-container" id="loan-enquiry">
            <Table hover className="custom-table align-middle text-nowrap">
              <thead>
                <tr>
                  {Object.entries(loanPaymentHeaders).map(([key, value]) => (
                    <th key={key}>
                      <div className="d-flex flex-row align-items-center justify-content-start gap-2">
                        <div
                          className="p-0 cursor-pointer"
                          onClick={() => {
                            handleAutoSort(key);
                          }}
                        >
                          {value}
                        </div>
                        <TableSortDisplayArrow
                          direction={checkColumnIfSorted(key)}
                        />
                        <button
                          className="p-0"
                          type="button"
                          style={{
                            background: 'none',
                            border: 'none',
                            color: '#fff',
                          }}
                          onClick={e => {
                            e.stopPropagation();
                            setColumnName(key);
                          }}
                        >
                          <FilterDropdown
                            handleSetColumnName={() => {
                              setColumnName(key);
                            }}
                            setColumnName={setColumnName}
                            columnData={columnData}
                            applyFilter={applyFilter}
                            columnName={columnName}
                            pageRequest={pageRequest}
                            setPageRequest={setPageRequest}
                            handleManualSort={handleManualSort}
                            isLoading={isLoadingColumn}
                          />
                        </button>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>{renderTableBody()}</tbody>
            </Table>
          </div>
        </CardBody>
        <CardFooter className="py-2">
          <TablePagination
            nextPage={nextPage}
            prevPage={prevPage}
            totalPages={data?.totalPages}
            currentPage={data?.currentPage}
            hasPreviousPage={data?.hasPreviousPage}
            hasNextPage={data?.hasNextPage}
            getPageSize={getPageSize}
            setPageSize={setPageSize}
            totalCount={data?.totalCount}
          />
        </CardFooter>
      </Card>
    </div>
  );
}

export default LoanEnquiry;
